<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12" md="3">
          <v-card elevation="0" border rounded="lg">
            <v-card-title class="text-subtitle-1 font-weight-bold">Requisition Info</v-card-title>
            <v-card-text>
              <v-select v-model="form.type" :items="requisitionTypes" item-title="title" item-value="value" label="Requisition Type *" variant="outlined" density="compact" :disabled="isEdit" @update:model-value="onTypeChange" />
              <v-autocomplete v-if="form.type === 'sale_order'" v-model="form.sale_order_master_id" :items="saleOrders" :item-title="saleOrderTitle" item-value="id" label="Approved Sales Order *" variant="outlined" density="compact" :disabled="isEdit" :menu-props="{ contentClass: 'my-custom-dropdown' }" @update:model-value="onSaleOrderChange" />
              <v-date-input v-model="form.requisition_date" label="Requisition Date *" variant="outlined" density="compact" prepend-icon="" />
              <v-textarea v-model="form.remarks" label="Remarks" variant="outlined" density="compact" rows="3" />

              <v-alert type="info" variant="tonal" density="compact">
                Planned finished quantity scales the template formula. The default template batch is 25 kg unless configured otherwise.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="9">
          <v-card elevation="0" border rounded="lg">
            <v-card-title class="d-flex align-center justify-space-between flex-wrap ga-2">
              <div>
                <div class="text-subtitle-1 font-weight-bold">Finished Products & Material Calculation</div>
                <div class="text-caption text-medium-emphasis">Set Present Purity to adjust physical raw-material requirements; packaging is planned separately.</div>
              </div>
              <v-autocomplete v-if="form.type === 'bulk'" v-model="selectedBulkProduct" :items="bulkProductOptions" :item-title="productOptionTitle" item-value="product_id" label="Add Finished Product" variant="outlined" density="compact" hide-details clearable style="max-width: 360px" :menu-props="{ contentClass: 'my-custom-dropdown' }" @update:model-value="addBulkProduct" />
            </v-card-title>

            <v-card-text>
              <v-expansion-panels v-if="form.cart.length" multiple>
                <v-expansion-panel v-for="(line, index) in form.cart" :key="line.uiKey">
                  <v-expansion-panel-title>
                    <div class="d-flex align-center justify-space-between flex-grow-1 pe-4">
                      <div>
                        <span class="font-weight-medium">{{ index + 1 }}. {{ line.product_code }} - {{ line.product_name }}</span>
                        <span class="text-caption text-medium-emphasis ms-2">{{ line.unit_name }}</span>
                      </div>
                      <div class="d-flex align-center ga-2">
                        <v-chip size="small" color="primary" variant="tonal">Plan {{ number(line.quantity, 4) }}</v-chip>
                        <v-chip v-if="line.calculation" size="small" color="info" variant="tonal">Scale {{ number(line.calculation.scale_factor, 6) }}×</v-chip>
                      </div>
                    </div>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <v-row>
                      <v-col cols="12" md="5">
                        <v-select v-model="line.template_id" :items="templatesFor(line.product_id)" :item-title="templateTitle" item-value="id" label="Production Template *" variant="outlined" density="compact" @update:model-value="onTemplateChange(line)" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field v-model.number="line.quantity" type="number" min="0.0001" step="0.0001" label="Planned Finished Qty *" variant="outlined" density="compact" @change="calculateLine(line)" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field :model-value="line.calculation?.default_batch_quantity || templateById(line.template_id)?.default_batch_quantity || 25" label="Template Batch Qty" variant="outlined" density="compact" readonly />
                      </v-col>
                      <v-col cols="12" md="1" class="d-flex align-center justify-end">
                        <v-btn v-if="form.type === 'bulk'" icon="mdi-delete-outline" variant="text" color="error" @click="removeLine(index)" />
                      </v-col>
                    </v-row>

                    <v-row dense class="mb-2">
                      <v-col cols="12" md="5">
                        <v-select v-model="line.balancerProductId" :items="balancerOptions(line)"
                          item-title="title" item-value="value" label="Batch Balancer (Salt / Remainder)"
                          variant="outlined" density="compact" hide-details
                          :disabled="templateHasBalancer(line.template_id)" @update:model-value="calculateLine(line)" />
                      </v-col>
                      <v-col cols="12" md="7" class="d-flex align-center">
                        <span class="text-caption text-medium-emphasis">Excel: Formula Qty × Std. Purity ÷ Present Purity; Balancer = planned batch − other RM quantities.</span>
                      </v-col>
                    </v-row>
                    <v-alert v-if="rawRequirements(line).some(row => row.calculation_method !== 'balancer' && !row.standard_purity)"
                      type="warning" variant="tonal" density="compact" class="mb-2">
                      Standard Purity is missing for one or more raw materials. Configure Excel values (e.g. 87%, 89%) in Production Settings → Templates; until then 100% baseline is used.
                    </v-alert>
                    <v-alert v-if="line.error" type="error" variant="tonal" density="compact" class="mb-3">{{ line.error }}</v-alert>

                    <div class="d-flex align-center ga-2 mb-2">
                      <div class="text-subtitle-2 font-weight-bold">Raw Material Formulation</div>
                      <v-chip size="x-small" color="success" variant="tonal">Present Purity: Editable (default 100%)</v-chip>
                    </div>
                    <div class="overflow-x-auto">
                      <v-table density="compact" class="calculation-table border-sm">
                        <thead>
                          <tr>
                            <th>Raw Material</th>
                            <th>Method</th>
                            <th class="text-end">Std. Purity %</th>
                            <th style="min-width: 116px">Present Purity %</th>
                            <th class="text-end">Formula Qty</th>
                            <th class="text-end">Loss %</th>
                            <th class="text-end">Loss Qty</th>
                            <th class="text-end">Physical Required</th>
                            <th class="text-end">Active Required</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="requirement in rawRequirements(line)" :key="requirement.raw_product_id">
                            <td>{{ rawProductLabel(line.template_id, requirement.raw_product_id) }}</td>
                            <td>{{ requirement.calculation_method === 'balancer' ? 'Balancer' : 'Fixed' }}</td>
                            <td class="text-end">{{ requirement.standard_purity ? number(requirement.standard_purity, 4) : '-' }}</td>
                            <td>
                              <!-- Native input intentionally avoids v-text-field disabled/inherited styling. -->
                              <div v-if="requirement.calculation_method !== 'balancer'" class="purity-edit-wrap my-1">
                                <input
                                  type="number"
                                  inputmode="decimal"
                                  min="0.0001"
                                  max="100"
                                  step="0.01"
                                  class="purity-edit-input"
                                  :aria-label="`Present Purity for ${rawProductLabel(line.template_id, requirement.raw_product_id)}`"
                                  :value="presentPurityValue(line, requirement)"
                                  @input="onPresentPurityInput(line, requirement, $event.target.value)"
                                />
                                <span class="purity-edit-symbol">%</span>
                              </div>
                              <span v-else class="text-caption text-medium-emphasis">Auto (Balancer)</span>
                            </td>
                            <td class="text-end">{{ number(requirement.formula_quantity ?? requirement.required_quantity, 6) }}</td>
                            <td class="text-end">{{ number(requirement.process_loss_percent, 4) }}%</td>
                            <td class="text-end">{{ number(requirement.loss_quantity, 6) }}</td>
                            <td class="text-end font-weight-bold">{{ number(requirement.total_required_quantity, 6) }}</td>
                            <td class="text-end">{{ number(requirement.active_required_quantity, 6) }}</td>
                          </tr>
                          <tr v-if="!rawRequirements(line).length">
                            <td colspan="9" class="text-center text-medium-emphasis py-4">Select a template and enter planned quantity.</td>
                          </tr>
                        </tbody>
                        <tfoot v-if="rawRequirements(line).length">
                          <tr class="subtotal-row">
                            <td colspan="4" class="font-weight-bold">Sub Total (RM)</td>
                            <td class="text-end font-weight-bold">{{ number(sumRaw(line, 'formula_quantity', 'required_quantity'), 6) }}</td>
                            <td class="text-end text-medium-emphasis">—</td>
                            <td class="text-end font-weight-bold">{{ number(sumRaw(line, 'loss_quantity'), 6) }}</td>
                            <td class="text-end font-weight-bold">{{ number(sumRaw(line, 'total_required_quantity'), 6) }}</td>
                            <td class="text-end font-weight-bold">{{ number(sumRaw(line, 'active_required_quantity'), 6) }}</td>
                          </tr>
                        </tfoot>
                      </v-table>
                    </div>
                    <div class="text-caption text-medium-emphasis mt-2">
                      Standard Purity comes from the Production Template (or Product Master fallback). Missing standards use 100% until configured. Present Purity changes the physical RM plan; Salt/Balancer keeps the batch mass fixed.
                    </div>
                    <div class="d-flex flex-wrap align-center ga-2 mt-5 mb-2">
                      <div class="text-subtitle-2 font-weight-bold">Packaging Materials</div>
                      <v-spacer />
                      <v-btn size="small" variant="tonal" color="secondary" prepend-icon="mdi-history"
                        :loading="line.historyLoading" @click="loadPreviousPackaging(line)">Load Previous (Same Product)</v-btn>
                      <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-plus"
                        @click="addPackaging(line)">Add Packaging</v-btn>
                    </div>
                    <v-alert v-if="packagingValidationMessage(line)" type="warning" variant="tonal" density="compact" class="mb-2">
                      {{ packagingValidationMessage(line) }} You can still adjust raw-material purity.
                    </v-alert>
                    <div class="overflow-x-auto">
                      <v-table density="compact" class="calculation-table border-sm packaging-table">
                        <thead>
                          <tr>
                            <th style="min-width: 280px">Packaging Product</th>
                            <th style="min-width: 140px">Required Qty</th>
                            <th class="text-end">Unit</th>
                            <th style="width: 58px"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(packaging, packageIndex) in line.packaging" :key="packaging.uiKey">
                            <td>
                              <v-autocomplete v-model="packaging.product_id" :items="packagingOptions(line)"
                                :item-title="productOptionTitle" item-value="product_id" label="Select Packaging"
                                variant="outlined" density="compact" hide-details="auto" class="my-1"
                                :menu-props="{ contentClass: 'my-custom-dropdown' }"
                                @update:model-value="onPackagingChange(line)"
                                @update:search="searchPackagingProducts" />
                            </td>
                            <td>
                              <v-text-field v-model.number="packaging.quantity" type="number" min="0.0001" step="0.0001"
                                label="Qty" variant="outlined" density="compact" hide-details="auto" class="my-1"
                                @change="onPackagingChange(line)" />
                            </td>
                            <td class="text-end">{{ packagingUnit(line, packaging.product_id) }}</td>
                            <td><v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" @click="removePackaging(line, packageIndex)" /></td>
                          </tr>
                          <tr v-if="!line.packaging.length"><td colspan="4" class="text-center text-medium-emphasis py-4">No packaging selected. Add items or load the previous package plan.</td></tr>
                        </tbody>
                        <tfoot v-if="packagingTotalsByUnit(line).length">
                          <tr v-for="subtotal in packagingTotalsByUnit(line)" :key="subtotal.unit" class="subtotal-row">
                            <td class="font-weight-bold">Sub Total (PM · {{ subtotal.unit }})</td>
                            <td class="font-weight-bold">{{ number(subtotal.quantity, 6) }}</td>
                            <td class="text-end font-weight-bold">{{ subtotal.unit }}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </v-table>
                    </div>
                    <div class="text-caption text-medium-emphasis mt-2">
                      Packaging quantities are for this planned production output. Actual inventory lots and their purity are verified at material issue.
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-alert v-else type="warning" variant="tonal">No finished-product line selected.</v-alert>
              <v-alert v-if="formError" type="error" variant="tonal" density="compact" class="mt-3">{{ formError }}</v-alert>
            </v-card-text>

            <v-divider />
            <v-card-actions class="justify-end">
              <v-btn variant="tonal" color="grey-darken-1" :loading="saving" @click="submit(0)">Save Draft</v-btn>
              <v-btn color="primary" :loading="saving" prepend-icon="mdi-send" @click="submit(1)">{{ isEdit ? 'Update & Submit' : 'Submit Requisition' }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

const store = useStore();
const router = useRouter();
const route = useRoute();
const saving = ref(false);
const isEdit = ref(false);
const requisitionId = ref(null);
const selectedBulkProduct = ref(null);
const packagingCatalog = ref([]);
let packagingSearchTimer = null;
const formError = ref("");

const form = reactive({
  type: "bulk",
  sale_order_master_id: null,
  requisition_date: new Date(),
  remarks: "",
  cart: [],
});

const requisitionTypes = [
  { title: "Bulk Production", value: "bulk" },
  { title: "Sales Order", value: "sale_order" },
];

const templates = computed(() => store.getters.getTemplateList || []);
const productOptions = computed(() => (store.getters.getProductMapList || []).filter(item => item?.status == 1 && item?.product));
const saleOrders = computed(() => store.getters.getPendingSaleOrderListForProduction || []);
const bulkProductOptions = computed(() => productOptions.value.filter(option => templatesFor(option.product_id).length));

const number = (value, decimals = 2) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: decimals });
const productOptionTitle = item => `${item?.product?.code || ""} - ${item?.product?.name || ""}`;
const saleOrderTitle = item => `${item?.code || "SO"}${item?.customer?.company_name ? ` - ${item.customer.company_name}` : ""}`;
const templateTitle = item => `${item.name} (v${item.version_no || 1}, batch ${number(item.default_batch_quantity || 25, 4)})`;
const templatesFor = productId => templates.value
  .filter(item => Number(item.product_id) === Number(productId) && Number(item.status) === 1)
  .sort((a, b) => Number(b.version_no || 1) - Number(a.version_no || 1) || Number(b.id) - Number(a.id));
const templateById = id => templates.value.find(item => Number(item.id) === Number(id));
const latestTemplate = productId => templatesFor(productId)[0];
const productMapById = id => productOptions.value.find(item => Number(item.product_id) === Number(id));

function templateHasBalancer(templateId) {
  return (templateById(templateId)?.items || []).some(item =>
    item.item_type !== "packaging" && item.calculation_method === "balancer");
}
function suggestedBalancerId(template) {
  const raw = (template?.items || []).filter(item => item.item_type !== "packaging");
  const explicit = raw.find(item => item.calculation_method === "balancer");
  if (explicit) return Number(explicit.raw_product_id);
  // Conservative legacy suggestion: one Salt and a mass-balanced formula.
  const candidates = raw.filter(item => /\bsalt\b/i.test(item.raw_product?.name || ""));
  const total = raw.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  return candidates.length === 1
    && Math.abs(total - Number(template?.default_batch_quantity || 25)) <= 0.01
    ? Number(candidates[0].raw_product_id) : null;
}
function balancerOptions(line) {
  const items = (templateById(line.template_id)?.items || []).filter(item => item.item_type !== "packaging");
  return [
    { title: "No Balancer (fixed quantities)", value: null },
    ...items.map(item => ({
      title: `${item.raw_product?.code || ""} - ${item.raw_product?.name || `Product #${item.raw_product_id}`}`,
      value: Number(item.raw_product_id),
    })),
  ];
}

function lineFromProduct(productId, quantity = 25, extra = {}) {
  const map = productMapById(productId);
  const product = map?.product || extra.product || {};
  const template = latestTemplate(productId);

  return {
    uiKey: `${Date.now()}-${Math.random()}`,
    product_id: Number(productId),
    sale_order_detail_id: extra.sale_order_detail_id || null,
    product_code: product.code || "",
    product_name: product.name || "",
    unit_name: product.unit?.name || "",
    quantity: Number(quantity || template?.default_batch_quantity || 25),
    template_id: extra.template_id || template?.id || null,
    calculation: null,
    purityOverrides: {},
    purityDrafts: {},
    balancerProductId: template ? suggestedBalancerId(template) : null,
    packaging: [],
    packagingCustomized: false,
    historyLoading: false,
    requestSequence: 0,
    purityTimer: null,
    error: "",
  };
}

function onTypeChange() {
  for (const line of form.cart) clearTimeout(line.purityTimer);
  form.sale_order_master_id = null;
  form.cart = [];
  selectedBulkProduct.value = null;
}

async function addBulkProduct(productId) {
  if (!productId) return;
  if (form.cart.some(line => Number(line.product_id) === Number(productId))) {
    toast.error("This finished product is already added.");
    selectedBulkProduct.value = null;
    return;
  }
  const template = latestTemplate(productId);
  if (!template) {
    toast.error("No active production template is configured for this product.");
    selectedBulkProduct.value = null;
    return;
  }
  const line = lineFromProduct(productId, template.default_batch_quantity || 25);
  form.cart.push(line);
  selectedBulkProduct.value = null;
  await calculateLine(line);
}

async function onSaleOrderChange(orderId) {
  for (const line of form.cart) clearTimeout(line.purityTimer);
  const order = saleOrders.value.find(item => Number(item.id) === Number(orderId));
  form.cart = [];
  if (!order) return;

  for (const detail of (order.details || []).filter(item => !item.parent_id)) {
    const template = latestTemplate(detail.product_id);
    const line = lineFromProduct(detail.product_id, detail.quantity, {
      product: detail.product,
      sale_order_detail_id: detail.id,
      template_id: template?.id,
    });
    form.cart.push(line);
    if (line.template_id) await calculateLine(line);
    else line.error = "No active template found for this Sales Order product.";
  }
}

function removeLine(index) {
  clearTimeout(form.cart[index]?.purityTimer);
  form.cart.splice(index, 1);
}

function rawRequirements(line) {
  return (line.calculation?.requirements || []).filter(item => item.item_type !== "packaging");
}

// Pure display summaries; never mutate the calculated requisition quantities.
// A percentage is not additive, so only quantity columns have totals.
function sumRaw(line, field, fallbackField = null) {
  return rawRequirements(line).reduce((total, requirement) => {
    const value = requirement[field] ?? (fallbackField ? requirement[fallbackField] : 0);
    const quantity = Number(value);
    return total + (Number.isFinite(quantity) ? quantity : 0);
  }, 0);
}

// Packaging may mix Kg/Pcs/other units. Keep those totals separate.
function packagingTotalsByUnit(line) {
  const byUnit = new Map();
  for (const row of line.packaging || []) {
    if (!row.product_id) continue;
    const quantity = Number(row.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    const unit = packagingUnit(line, row.product_id);
    byUnit.set(unit, (byUnit.get(unit) || 0) + quantity);
  }
  return [...byUnit].map(([unit, quantity]) => ({ unit, quantity }));
}

function packageRow(productId = null, quantity = 1, product = null) {
  return { uiKey: `${Date.now()}-${Math.random()}`, product_id: productId ? Number(productId) : null, quantity: Number(quantity), product };
}

function packagingOptions(line) {
  const options = [...productOptions.value];
  for (const product of packagingCatalog.value) {
    if (!options.some(item => Number(item.product_id) === Number(product.id))) {
      options.push({ product_id: product.id, product });
    }
  }
  for (const row of line.packaging) {
    if (row.product && !options.some(item => Number(item.product_id) === Number(row.product_id))) {
      options.push({ product_id: row.product_id, product: row.product });
    }
  }
  return options;
}

async function fetchPackagingProducts(search = "") {
  try {
    const response = await store.dispatch("getProductionMaterialProducts", search);
    packagingCatalog.value = response.data || [];
  } catch (error) {
    // Product selection from the mapped products remains available if search fails.
    console.warn("Packaging Product Master search failed", error);
  }
}

function searchPackagingProducts(search) {
  clearTimeout(packagingSearchTimer);
  if (!search || String(search).length >= 2) {
    packagingSearchTimer = setTimeout(() => fetchPackagingProducts(search || ""), 250);
  }
}

function packagingUnit(line, productId) {
  const product = packagingOptions(line).find(item => Number(item.product_id) === Number(productId))?.product;
  return product?.unit?.name || "-";
}

function onTemplateChange(line) {
  clearTimeout(line.purityTimer);
  line.requestSequence++;
  line.purityOverrides = {};
  line.purityDrafts = {};
  line.balancerProductId = suggestedBalancerId(templateById(line.template_id));
  line.packaging = [];
  line.packagingCustomized = false;
  calculateLine(line);
}

function presentPurityValue(line, requirement) {
  const id = requirement.raw_product_id;
  // Keep the raw string while editing (including temporary empty/partial values).
  // Otherwise Vue replaces the input during typing, making it look read-only.
  if (Object.prototype.hasOwnProperty.call(line.purityDrafts, id)) return line.purityDrafts[id];
  return line.purityOverrides[id] ?? requirement.present_purity ?? 100;
}

// Apply a valid Present Purity without waiting for blur. The debounce avoids
// unnecessary API calls; sequence protects against out-of-order responses.
function onPresentPurityInput(line, requirement, input) {
  clearTimeout(line.purityTimer);
  line.requestSequence++;
  line.purityDrafts[requirement.raw_product_id] = input;
  const value = Number(input);
  if (input === null || input === "" || !Number.isFinite(value) || value <= 0 || value > 100) {
    line.error = "Present Purity must be greater than 0 and at most 100%.";
    return;
  }
  line.purityOverrides[requirement.raw_product_id] = value;
  line.error = "";
  line.purityTimer = setTimeout(() => {
    line.purityTimer = null;
    calculateLine(line);
  }, 300);
}

function addPackaging(line) {
  line.packaging.push(packageRow(null, 1));
  line.packagingCustomized = true;
}

function removePackaging(line, index) {
  line.packaging.splice(index, 1);
  onPackagingChange(line);
}

function packagingValidationMessage(line) {
  if (line.packaging.some(row => !row.product_id || !Number.isFinite(Number(row.quantity)) || Number(row.quantity) <= 0)) {
    return "Complete every packaging item before saving.";
  }
  const ids = line.packaging.map(row => Number(row.product_id));
  return new Set(ids).size === ids.length ? "" : "The same packaging product cannot be added twice.";
}

function validPackagingItems(line) {
  // Packaging editing is independent of raw-material purity preview.
  // Ignore unfinished/duplicate rows only for preview; Save still rejects them.
  const seen = new Set();
  return line.packaging.filter(row => {
    const id = Number(row.product_id);
    if (!id || seen.has(id) || !Number.isFinite(Number(row.quantity)) || Number(row.quantity) <= 0) return false;
    seen.add(id);
    return true;
  }).map(row => ({ product_id: Number(row.product_id), quantity: Number(row.quantity) }));
}

function onPackagingChange(line) {
  line.packagingCustomized = true;
  calculateLine(line);
}

async function loadPreviousPackaging(line) {
  line.historyLoading = true;
  try {
    const response = await store.dispatch("getProductionPackagingHistory", line.product_id);
    const previous = response.data;
    if (!previous?.items?.length || Number(previous.finished_quantity) <= 0) {
      toast.info("No earlier submitted packaging plan exists for this finished product.");
      return;
    }
    const scale = Number(line.quantity) / Number(previous.finished_quantity);
    line.packaging = previous.items.map(item => packageRow(item.product_id, Number((Number(item.quantity) * scale).toFixed(4)), item.product));
    line.packagingCustomized = true;
    await calculateLine(line);
    toast.success("Previous packaging loaded and scaled to the planned finished quantity.");
  } catch (error) {
    line.error = error?.response?.data?.message || "Unable to load the previous packaging plan.";
  } finally {
    line.historyLoading = false;
  }
}

async function calculateLine(line) {
  const sequence = ++line.requestSequence;
  line.error = "";
  if (!line.template_id || Number(line.quantity) <= 0) {
    line.calculation = null;
    return;
  }

  const template = templateById(line.template_id);
  for (const item of template?.items || []) {
    if (item.item_type === "packaging" || item.calculation_method === "balancer"
      || Number(item.raw_product_id) === Number(line.balancerProductId)) continue;
    if (line.purityOverrides[item.raw_product_id] == null) {
      line.purityOverrides[item.raw_product_id] = 100;
    }
  }

  try {
    const response = await store.dispatch("calculateProductionTemplate", {
      id: line.template_id,
      planned_output_quantity: Number(line.quantity),
      present_purities: line.purityOverrides,
      ...(line.balancerProductId ? { balancer_product_id: Number(line.balancerProductId) } : {}),
      ...(line.packagingCustomized ? { packaging_items: validPackagingItems(line) } : {}),
    });
    if (sequence !== line.requestSequence) return;
    line.calculation = response.data;
    if (!line.packagingCustomized) {
      line.packaging = (line.calculation.requirements || [])
        .filter(item => item.item_type === "packaging")
        .map(item => packageRow(item.raw_product_id, item.total_required_quantity,
          template?.items?.find(row => Number(row.raw_product_id) === Number(item.raw_product_id))?.raw_product));
    }
  } catch (error) {
    if (sequence === line.requestSequence) {
      line.error = error?.response?.data?.message || "Material calculation failed.";
    }
  }
}

function rawProductLabel(templateId, rawProductId) {
  const item = templateById(templateId)?.items?.find(row => Number(row.raw_product_id) === Number(rawProductId));
  const product = item?.raw_product || productMapById(rawProductId)?.product;
  return product ? `${product.code ? `${product.code} - ` : ""}${product.name || ""}` : `Product #${rawProductId}`;
}

function validate() {
  formError.value = "";
  if (!form.type) formError.value = "Requisition type is required.";
  else if (form.type === "sale_order" && !form.sale_order_master_id) formError.value = "Select an approved Production Sales Order.";
  else if (!form.requisition_date) formError.value = "Requisition date is required.";
  else if (!form.cart.length) formError.value = "Add at least one finished product.";
  else if (form.cart.some(line => !line.template_id || Number(line.quantity) <= 0)) formError.value = "Every finished-product line needs an active template and planned quantity.";
  else if (form.cart.some(line => line.error || !line.calculation)) formError.value = "Resolve material-calculation errors before saving.";
  else if (form.cart.some(line => packagingValidationMessage(line))) formError.value = form.cart.map(packagingValidationMessage).find(Boolean);
  else if (form.cart.some(line => rawRequirements(line).some(row => row.calculation_method !== "balancer" && (
    !Number.isFinite(Number(line.purityOverrides[row.raw_product_id] ?? 100)) ||
    Number(line.purityOverrides[row.raw_product_id] ?? 100) <= 0 ||
    Number(line.purityOverrides[row.raw_product_id] ?? 100) > 100
  )))) formError.value = "Present Purity must be greater than 0 and at most 100%.";
  return !formError.value;
}

async function submit(status) {
  if (!validate()) return;
  saving.value = true;
  const payload = {
    type: form.type,
    sale_order_master_id: form.type === "sale_order" ? form.sale_order_master_id : null,
    requisition_date: formattedDate(form.requisition_date),
    remarks: form.remarks || null,
    status,
    cart: form.cart.map(line => ({
      product_id: line.product_id,
      sale_order_detail_id: line.sale_order_detail_id,
      template_id: line.template_id,
      quantity: Number(line.quantity),
      present_purities: line.purityOverrides,
      balancer_product_id: line.balancerProductId || null,
      packaging_items: line.packaging.map(item => ({
        product_id: Number(item.product_id), quantity: Number(item.quantity),
      })),
    })),
  };

  try {
    if (isEdit.value) {
      await store.dispatch("updateRawMetarialRequisition", { ...payload, id: requisitionId.value });
    } else {
      await store.dispatch("saveRawMetarialRequisition", payload);
    }
    toast.success(status ? "Production requisition submitted." : "Production requisition saved as draft.");
    router.push({ name: "production-requisition-list" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    formError.value = errors ? Object.values(errors).flat().join("\n") : error?.response?.data?.message || "Unable to save requisition.";
    toast.error(formError.value);
  } finally {
    saving.value = false;
  }
}

async function loadRequisition(id) {
  const response = await store.dispatch("getRawMetarialRequisitionById", id);
  const data = response.data;
  Object.assign(form, {
    type: data.type,
    sale_order_master_id: data.sale_order_master_id,
    requisition_date: data.requisition_date ? new Date(data.requisition_date) : null,
    remarks: data.remarks || "",
    cart: (data.details || []).map(detail => {
      const line = lineFromProduct(detail.product_id, detail.quantity, {
        product: detail.product,
        sale_order_detail_id: detail.sale_order_detail_id,
        template_id: detail.template_id,
      });
      line.balancerProductId = Number((detail.raw_product_details || [])
        .find(row => row.item_type !== "packaging" && row.calculation_method === "balancer")?.raw_product_id) || null;
      line.purityOverrides = Object.fromEntries((detail.raw_product_details || [])
        .filter(row => row.item_type !== "packaging" && row.calculation_method !== "balancer")
        .map(row => [row.raw_product_id, Number(row.present_purity ?? 100)]));
      line.packaging = (detail.raw_product_details || [])
        .filter(row => row.item_type === "packaging")
        .map(row => packageRow(row.raw_product_id, row.total_required_quantity, row.raw_product));
      line.packagingCustomized = true;
      line.calculation = {
        default_batch_quantity: Number(detail.default_batch_quantity || 25),
        planned_output_quantity: Number(detail.quantity || 0),
        scale_factor: Number(detail.quantity || 0) / Number(detail.default_batch_quantity || 25),
        requirements: (detail.raw_product_details || []).map(row => ({
          raw_product_id: row.raw_product_id,
          item_type: row.item_type,
          calculation_method: row.calculation_method,
          standard_purity: row.standard_purity,
          present_purity: row.present_purity ?? 100,
          formula_quantity: Number(templateById(detail.template_id)?.items?.find(item => Number(item.raw_product_id) === Number(row.raw_product_id))?.quantity || row.required_quantity) * (Number(detail.quantity) / Number(detail.default_batch_quantity || 25)),
          required_quantity: row.required_quantity,
          process_loss_percent: row.process_loss_percent,
          loss_quantity: row.loss_quantity,
          total_required_quantity: row.total_required_quantity,
          active_required_quantity: row.active_required_quantity,
        })),
      };
      return line;
    }),
  });
}

onBeforeUnmount(() => {
  clearTimeout(packagingSearchTimer);
  for (const line of form.cart) clearTimeout(line.purityTimer);
});

onMounted(async () => {
  saving.value = true;
  try {
    await Promise.all([
      store.dispatch("getProductMapList"),
      store.dispatch("getTemplateList"),
      store.dispatch("getPendingSaleOrderListForProduction", { production_status: "Pending" }),
      fetchPackagingProducts(),
    ]);
    if (route.query.id) {
      isEdit.value = true;
      requisitionId.value = route.query.id;
      await loadRequisition(requisitionId.value);
    }
  } catch (error) {
    toast.error(error?.response?.data?.message || "Failed to load Production requisition data.");
  } finally {
    saving.value = false;
  }
});
</script>

<style scoped>
.calculation-table :deep(th) { white-space: nowrap; }
.calculation-table :deep(tfoot td) {
  background: #edf3f8;
  border-top: 2px solid #bccad8;
  color: #243447;
  padding-top: 10px;
  padding-bottom: 10px;
}
.calculation-table :deep(td) { white-space: nowrap; }
.calculation-table :deep(th) { background: #f1f4f8; }
.purity-edit-wrap {
  display: flex;
  align-items: center;
  min-width: 118px;
  border: 1px solid #9aa8b7;
  border-radius: 5px;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.purity-edit-wrap:focus-within {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.14);
}
.purity-edit-input {
  width: 86px;
  min-width: 0;
  padding: 7px 8px;
  color: #202a36;
  background: transparent;
  outline: none;
  border: 0;
  font: inherit;
  font-size: 13px;
  cursor: text;
}
.purity-edit-symbol { padding-right: 9px; color: #66788a; }
.packaging-table { max-width: 900px; }
</style>
